import * as React from 'react';
import { useState, useEffect } from 'react';
import Service from '../services/Service';
import {
    Button,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Chip,
    Box,
    Tooltip
} from '@mui/material';
import { Edit as EditIcon, Visibility as VisibilityIcon, UploadFile as UploadFileIcon,  FileDownload as FileDownloadIcon } from '@mui/icons-material';
import ConfigUserFormDialog from './ConfigUserFormDialog';
import { Link, useNavigate } from 'react-router-dom';
import {
    buildConfigUserPayload,
    downloadJsonFile,
    normalizeImportedConfigUser,
    parseImportedConfigUsers
} from './configUserImportExport';
import { ROLES } from '../auth/roles';
import { hasRoleOrGroup } from '../auth/access';

export default function ConfigUserList(props) {
    const { setWait } = props;
    const [configUsers, setConfigUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);

    const navigate = useNavigate();

    // Role "visualizador" (ou nenhuma role/grupo cadastrado no Keycloak):
    // enxerga só os próprios registros (o backend já filtra isso), mas não
    // pode criar/editar/apagar nada — só os botões de ação ficam escondidos
    // aqui; a garantia de verdade é no backend (ConfigUserService), que
    // rejeita a alteração mesmo que alguém force a chamada da API.
    const podeEditar = hasRoleOrGroup(ROLES.ADMIN)
        || hasRoleOrGroup(ROLES.DEV)
        || hasRoleOrGroup(ROLES.USER);

    useEffect(() => {
        loadConfigUsers();
    }, []);

    const loadConfigUsers = () => {
        setWait(true);
        Service.GET("/api/configUser/list").then(response => {
            if (response && response.length > 0) {
                setConfigUsers(response);

            } else {
                console.log("Erro:", response);
            }

            setWait(false);
        }).catch(error => {
            if (error.message === 'duplicado') return;
            console.error(error);
            setWait(false);
        });
    };

    const handleNovoItem = () => {
        setSelectedUser(null);
        setDialogOpen(true);
    };

    const handleEditarItem = (user) => {
        setSelectedUser(user);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedUser(null);
    };

    const handleSaveSuccess = () => {
        loadConfigUsers();
    };

    const handleExport = (user) => {
        const payload = buildConfigUserPayload(user);
        downloadJsonFile(payload, `${(user?.nome || 'config-user').replace(/\s+/g, '-').toLowerCase()}.json`);
    };

    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const text = await file.text();
            const parsedUsers = parseImportedConfigUsers(text);
            const normalizedUsers = parsedUsers.map(normalizeImportedConfigUser);

            const promises = normalizedUsers.map((userPayload) =>
                Service.POST('/api/configUser', userPayload)
            );

            await Promise.all(promises);
            await loadConfigUsers();
            event.target.value = '';
        } catch (error) {
            console.error(error);
            alert(error?.message || 'Não foi possível importar o arquivo.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ p: 4 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h4" component="h1">
                            Configurações de Monitoramento
                        </Typography>
                        {podeEditar && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    color="primary"
                                    disabled={importing}
                                >
                                    <FileDownloadIcon />
                                    {importing ? 'Importando…' : 'Importar'}
                                    <input
                                        type="file"
                                        accept="application/json"
                                        hidden
                                        onChange={handleImport}
                                    />
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleNovoItem}
                                >
                                    Novo Item
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Nome</strong></TableCell>
                                    {/* <TableCell><strong>URL de Referência</strong></TableCell> */}
                                    <TableCell><strong>Emails</strong></TableCell>
                                    <TableCell><strong>Filtros</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configUsers.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.nome}</TableCell>
                                        {/* <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {user.urlRef}
                                            </Typography>
                                        </TableCell> */}
                                        <TableCell>
                                            {user.emails && user.emails.slice(0, 2).map((email, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={email}
                                                    size="small"
                                                    sx={{ m: 0.5 }}
                                                />
                                            ))}
                                            {user.emails && user.emails.length > 2 && (
                                                <Chip
                                                    label={`+${user.emails.length - 2}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {user.filtros && user.filtros.map((filtro, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={filtro.nome}
                                                    size="small"
                                                    sx={{ m: 0.5 }}
                                                    color={filtro.tipo === 'EXCLUSIVO' ? 'error' : 'default'}
                                                />
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.active ? "Ativo" : "Inativo"}
                                                color={user.active ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Visualizar">
                                                    <IconButton
                                                        component={Link}
                                                        color="info"
                                                        to={`/configUser/view?id=${user.id}`}
                                                        size="small"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {podeEditar && (
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => handleEditarItem(user)}
                                                            size="small"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* <Tooltip title="Exportar item">
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => handleExport(user)}
                                                        size="small"
                                                    >
                                                        < UploadFileIcon />
                                                    </IconButton>
                                                </Tooltip> */}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Dialog de edição/criação */}
            <ConfigUserFormDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                userData={selectedUser}
                setWait={setWait}
                onSaveSuccess={handleSaveSuccess}
                handleExport={handleExport}
            />
        </Container>
    );
}